import { useState } from 'react'

import {
  normaliserConstats,
  normaliserExperiencesEcartees,
  verifierRapport,
  versionSupportee,
  type Certification,
  type Colonne,
  type DonneesRapport,
  type LigneGrille,
  type Totaux,
} from '../lib/rapport'

/**
 * Rendu du rapport de diagnostic, depuis `rapports.donnees`.
 *
 * Rien n'est stocke en HTML : l'application rend le JSON, et le PDF sort de
 * l'impression de cette page.
 */

const ETIQUETTES: Record<'actuel' | 'refonte' | 'certifie', string> = {
  actuel: "Votre CV aujourd'hui",
  refonte: 'Avec le CV refondu que nous vous remettons',
  certifie: 'Avec vos trois certifications bureautiques',
}

export function RapportDiagnostic({
  donnees,
  versionFormat,
}: {
  donnees: unknown
  versionFormat: string
}) {
  // Deux refus possibles, et aucun des deux ne rend le rapport « au mieux ».
  if (!versionSupportee(versionFormat)) {
    return (
      <BandeauAnomalie
        titre="Format de rapport inconnu"
        motif={`Ce rapport annonce le format « ${versionFormat} », que cette version de l'application ne sait pas rendre.`}
      />
    )
  }

  const verification = verifierRapport(donnees)

  if (!verification.coherent) {
    return <BandeauAnomalie titre="Données incohérentes" motif={verification.motif} />
  }

  return <Rapport donnees={verification.donnees} totaux={verification.totaux} />
}

/**
 * Bandeau d'anomalie.
 *
 * Le rapport n'est PAS rendu a cote : un rapport faux presente a un apprenant en
 * entretien est le pire incident que cette application puisse produire. Le
 * generateur Python refuse deja de produire un tel rapport ; cet ecran refuse de
 * l'afficher, avec le meme controle et les memes seuils.
 */
function BandeauAnomalie({ titre, motif }: { titre: string; motif: string }) {
  return (
    <section
      role="alert"
      data-test="anomalie-rapport"
      className="rounded-lg border-2 border-score-actuel bg-red-50 p-6"
    >
      <h2 className="text-lg font-semibold text-score-actuel">{titre}</h2>
      <p className="mt-2 text-slate-800">
        Le rapport n’est pas affiché. Prévenez un administrateur avant de présenter ce dossier.
      </p>
      <p className="mt-3 rounded bg-white p-3 font-mono text-sm text-slate-700">{motif}</p>
    </section>
  )
}

function Rapport({ donnees, totaux }: { donnees: DonneesRapport; totaux: Totaux }) {
  return (
    <div data-test="rapport" className="space-y-10">
      {donnees.constat_ouverture ? (
        <p className="max-w-3xl text-lg leading-relaxed text-slate-800">
          {donnees.constat_ouverture}
        </p>
      ) : null}

      <BlocsScores donnees={donnees} />

      {donnees.argumentaire ? (
        <p className="max-w-3xl leading-relaxed text-slate-800">{donnees.argumentaire}</p>
      ) : null}

      <GrilleNotation donnees={donnees} totaux={totaux} />

      <SectionCertifications donnees={donnees} />

      <ListesConstats donnees={donnees} />

      <ExperiencesEcartees donnees={donnees} />

      {donnees.citation ? (
        <blockquote className="max-w-3xl border-l-4 border-marque-primaire pl-4 text-lg italic leading-relaxed text-slate-800">
          {donnees.citation}
        </blockquote>
      ) : null}
    </div>
  )
}

/**
 * Les trois scores.
 *
 * Chaque bloc porte SON CHIFFRE ET SON INTITULE. La couleur ne transporte
 * jamais seule l'information : un daltonien, ou un portable mal calibre dans une
 * salle de rendez-vous, doit comprendre sans distinguer les teintes.
 */
function BlocsScores({ donnees }: { donnees: DonneesRapport }) {
  // Le troisieme score est replie au chargement, et son etat n'est pas
  // memorise : chaque ouverture de page repart replie, parce que c'est le
  // conseiller qui choisit le moment ou il le montre.
  //
  // Cette commande est DISTINCTE de celle de la section des certifications, et
  // ne doit pas lui etre couplee : on revele le score, puis le detail, a deux
  // moments differents de l'entretien.
  const [cibleVisible, setCibleVisible] = useState(false)

  const blocs = [
    {
      cle: 'actuel' as const,
      valeur: donnees.scores.actuel,
      etiquette: donnees.etiq_actuel ?? ETIQUETTES.actuel,
      detail: donnees.detail_actuel,
      chiffre: 'text-score-actuel',
      jauge: 'bg-score-actuel',
      cadre: 'border-score-actuel/40 bg-score-actuel/[0.06]',
    },
    {
      cle: 'refonte' as const,
      valeur: donnees.scores.refonte,
      etiquette: donnees.etiq_refonte ?? ETIQUETTES.refonte,
      detail: donnees.detail_refonte,
      // L'ambre de jauge est a 3,93:1 : il ne porte pas de texte. Le chiffre
      // utilise sa variante assombrie, mesuree a 5,87:1.
      chiffre: 'text-score-refonte-texte',
      jauge: 'bg-score-refonte',
      cadre: 'border-score-refonte/40 bg-score-refonte/[0.07]',
    },
    {
      cle: 'certifie' as const,
      valeur: donnees.scores.certifie,
      etiquette: donnees.etiq_certifie ?? ETIQUETTES.certifie,
      detail: donnees.detail_certifie,
      chiffre: 'text-score-certifie',
      jauge: 'bg-score-certifie',
      cadre: 'border-score-certifie/40 bg-score-certifie/[0.07]',
    },
  ]

  // La troisieme carte n'est pas masquee en CSS : elle n'est pas rendue du tout.
  // Absente du DOM, donc absente du PDF — et absente de ce qu'un curieux
  // pourrait lire dans la page avant que le conseiller ne la montre.
  const blocsVisibles = cibleVisible ? blocs : blocs.slice(0, 2)

  return (
    <section aria-labelledby="titre-scores" data-test="scores">
      <h2 id="titre-scores" className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        Les trois scores
      </h2>

      {/* Deux colonnes quand la troisieme carte est repliee : sans cela les deux
          cartes restantes garderaient chacune un tiers de la largeur, et la
          rangee paraitrait amputee. */}
      <div className={`mt-4 grid gap-4 ${cibleVisible ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        {blocsVisibles.map((bloc) => (
          <article
            key={bloc.cle}
            id={bloc.cle === 'certifie' ? 'score-certifie' : undefined}
            data-test={`score-${bloc.cle}`}
            className={`eviter-coupure rounded-lg border p-5 ${bloc.cadre}`}
          >
            <p className={`text-4xl font-bold tabular-nums ${bloc.chiffre}`}>
              {bloc.valeur}
              <span className="text-xl font-semibold"> / 100</span>
            </p>
            <p className="mt-2 font-semibold text-slate-900">{bloc.etiquette}</p>
            {bloc.detail ? <p className="mt-2 text-sm text-slate-700">{bloc.detail}</p> : null}

            <div className="mt-4 h-2 w-full rounded-full bg-slate-200" aria-hidden="true">
              <div
                className={`h-2 rounded-full ${bloc.jauge}`}
                style={{ width: `${Math.max(0, Math.min(100, bloc.valeur))}%` }}
              />
            </div>
          </article>
        ))}
      </div>

      <button
        type="button"
        data-test="bascule-score-certifie"
        aria-expanded={cibleVisible}
        aria-controls="score-certifie"
        onClick={() => setCibleVisible((etat) => !etat)}
        className="sans-impression mt-4 rounded bg-marque-primaire-texte px-4 py-2 font-medium text-white hover:bg-marque-primaire-survol"
      >
        {cibleVisible
          ? 'Masquer le score avec les trois certifications'
          : 'Afficher le score avec les trois certifications'}
      </button>
    </section>
  )
}

/**
 * La grille.
 *
 * C'est elle qui rend les trois scores credibles : le conseiller doit pouvoir
 * pointer une ligne du doigt pendant l'entretien.
 */
function GrilleNotation({ donnees, totaux }: { donnees: DonneesRapport; totaux: Totaux }) {
  const colonnes: { cle: Colonne; titre: string }[] = [
    { cle: 'max', titre: 'Maximum' },
    { cle: 'actuel', titre: 'Actuel' },
    { cle: 'refonte', titre: 'Refonte' },
    { cle: 'certifie', titre: 'Certifié' },
  ]

  return (
    <section aria-labelledby="titre-grille" data-test="grille">
      <h2 id="titre-grille" className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        Le détail, critère par critère
      </h2>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="border-b-2 border-slate-300 text-slate-600">
              <th scope="col" className="py-2 pr-4 font-medium">Critère</th>
              {colonnes.map((colonne) => (
                <th key={colonne.cle} scope="col" className="py-2 pl-4 text-right font-medium">
                  {colonne.titre}
                </th>
              ))}
            </tr>
          </thead>

          <BlocGrille titre="Maîtrise bureautique" lignes={donnees.grille.bureautique} totaux={totaux} champ="bureautique" colonnes={colonnes} />
          <BlocGrille titre="Conformité du CV" lignes={donnees.grille.conformite} totaux={totaux} champ="conformite" colonnes={colonnes} />

          <tfoot>
            <tr className="eviter-coupure border-t-2 border-slate-400 font-bold text-slate-900">
              <td className="py-2 pr-4">Total</td>
              {colonnes.map((colonne) => (
                <td key={colonne.cle} className="py-2 pl-4 text-right tabular-nums">
                  {totaux[colonne.cle].total}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  )
}

function BlocGrille({
  titre,
  lignes,
  totaux,
  champ,
  colonnes,
}: {
  titre: string
  lignes: readonly LigneGrille[]
  totaux: Totaux
  champ: 'bureautique' | 'conformite'
  colonnes: { cle: Colonne; titre: string }[]
}) {
  return (
    <tbody>
      {lignes.map((ligne, index) => (
        <tr key={`${champ}-${index}`} className="eviter-coupure border-b border-slate-200">
          <td className="py-2 pr-4 text-slate-800">{ligne.libelle}</td>
          {colonnes.map((colonne) => (
            <td key={colonne.cle} className="py-2 pl-4 text-right tabular-nums text-slate-700">
              {ligne[colonne.cle]}
            </td>
          ))}
        </tr>
      ))}
      <tr className="eviter-coupure border-b border-slate-300 font-semibold text-slate-900">
        <td className="py-2 pr-4">{titre}</td>
        {colonnes.map((colonne) => (
          <td key={colonne.cle} className="py-2 pl-4 text-right tabular-nums">
            {totaux[colonne.cle][champ]}
          </td>
        ))}
      </tr>
    </tbody>
  )
}

/**
 * Les trois certifications — REPLIEE AU CHARGEMENT.
 *
 * Demande explicite du commanditaire. L'etat n'est pas memorise : chaque
 * ouverture de page repart replie, parce que c'est le conseiller qui choisit le
 * moment ou il l'affiche, face a l'apprenant.
 *
 * Le bouton est nomme et dit ce qu'il revele : une fleche muette obligerait a
 * cliquer pour savoir, ce qui est exactement ce qu'on veut eviter en entretien.
 */
function SectionCertifications({ donnees }: { donnees: DonneesRapport }) {
  const [ouverte, setOuverte] = useState(false)
  const certifications = donnees.certifications ?? []

  if (certifications.length === 0 && !donnees.chapo_certifications) return null

  return (
    <section
      aria-labelledby="titre-certifications"
      data-test="section-certifications"
      // Repliee, la section ne laisse a l'impression qu'un titre suivi de rien,
      // le bouton etant masque. Un titre orphelin dans un document remis a
      // l'apprenant est pire que l'absence de section : on retire l'ensemble.
      className={ouverte ? undefined : 'sans-impression'}
    >
      <h2
        id="titre-certifications"
        className="text-sm font-semibold uppercase tracking-wide text-slate-500"
      >
        Les trois certifications
      </h2>

      <button
        type="button"
        data-test="bascule-certifications"
        aria-expanded={ouverte}
        aria-controls="contenu-certifications"
        onClick={() => setOuverte((etat) => !etat)}
        className="sans-impression mt-3 rounded bg-marque-primaire-texte px-4 py-2 font-medium text-white hover:bg-marque-primaire-survol"
      >
        {ouverte
          ? 'Masquer les trois certifications'
          : `Afficher les trois certifications (${certifications.length})`}
      </button>

      {ouverte ? (
        <div id="contenu-certifications" data-test="contenu-certifications" className="mt-4 space-y-4">
          {donnees.chapo_certifications ? (
            <p className="max-w-3xl leading-relaxed text-slate-800">
              {donnees.chapo_certifications}
            </p>
          ) : null}

          <ul className="grid gap-4 md:grid-cols-3">
            {certifications.map((certification: Certification) => (
              <li
                key={certification.nom}
                className="eviter-coupure rounded-lg border border-slate-200 bg-white p-4"
              >
                <p className="font-semibold text-slate-900">{certification.nom}</p>
                <p className="mt-1 text-sm font-medium text-marque-primaire-texte">
                  {certification.points} points
                </p>
                <p className="mt-2 text-sm text-slate-700">{certification.intitule}</p>
                {certification.description ? (
                  <p className="mt-2 text-sm text-slate-600">{certification.description}</p>
                ) : null}
                {certification.code_rs ? (
                  <p className="mt-2 text-xs text-slate-500">
                    Répertoire Spécifique — {certification.code_rs}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>

          {donnees.note_certifications ? (
            <p className="max-w-3xl leading-relaxed text-slate-800">
              {donnees.note_certifications}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

function ListesConstats({ donnees }: { donnees: DonneesRapport }) {
  // Les rubriques n'ont pas toutes la meme forme dans le format du plugin :
  // `corriges` et `atouts` portent { titre, texte }, `plan_action` des chaines.
  // normaliserConstats ramene les deux au meme objet.
  const listes = [
    { titre: 'Ce qui a été corrigé', elements: normaliserConstats(donnees.corriges) },
    { titre: 'Ce qui joue en votre faveur', elements: normaliserConstats(donnees.atouts) },
    { titre: 'Les prochaines étapes', elements: normaliserConstats(donnees.plan_action) },
  ].filter((liste) => liste.elements.length > 0)

  if (listes.length === 0) return null

  return (
    <div className="grid gap-8 md:grid-cols-3">
      {listes.map((liste) => (
        <section key={liste.titre} data-test="liste-constats" className="eviter-coupure">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            {liste.titre}
          </h2>
          <ul className="mt-3 space-y-3">
            {liste.elements.map((element, index) => (
              <li key={index} className="flex gap-2 text-sm text-slate-800">
                <span
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-marque-primaire"
                  aria-hidden="true"
                />
                <span>
                  {element.titre === null ? null : (
                    <strong className="block font-semibold text-slate-900">{element.titre}</strong>
                  )}
                  {element.texte}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

/**
 * Postes retires du CV pour qu'il tienne sur une page (plugin 0.6.2).
 *
 * Rien ne disparait sans trace : le conseiller les voit et les rajoute en
 * entretien si l'offre visee les valorise.
 */
function ExperiencesEcartees({ donnees }: { donnees: DonneesRapport }) {
  const elements = normaliserExperiencesEcartees(donnees.experiences_ecartees)
  if (elements.length === 0) return null

  return (
    <section data-test="experiences-ecartees" aria-labelledby="titre-ecartees" className="eviter-coupure">
      <h2 id="titre-ecartees" className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        Expériences non reprises sur le CV
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-slate-700">
        Pour que le CV tienne sur une page, ces expériences n’y figurent pas. Elles restent les
        vôtres : on les rajoute si l’offre visée les valorise.
      </p>
      <ul className="mt-3 space-y-2">
        {elements.map((element, index) => (
          <li key={index} className="flex gap-2 text-sm text-slate-800">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-marque-primaire" aria-hidden="true" />
            <span>
              <strong className="font-semibold text-slate-900">{element.intitule}</strong>
              {element.dates === null ? null : <span className="text-slate-600"> ({element.dates})</span>}
              {element.motif === null ? null : <span className="text-slate-600"> · {element.motif}</span>}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}
